import { c as _c } from "react/compiler-runtime";
import { Button, FullWidthBanner } from '@systemeio/components';
import { XCloseIcon } from '@systemeio/icons';
import { CurrencyEnum } from '@systemeio/types';
import { centsToUnits, useSessionStore } from '@systemeio/util';
import { useTotalRevenueLimit } from 'modules/main/hooks/use-total-revenue-limit';
import { useMemo, useState } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { CONTENT_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { currencyFormat } from 'shared/utils/currency-format';
import { dateFormat } from 'shared/utils/date-time-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
const MIN_REVENUE_LIMIT_PERCENTAGE = 80;
const REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY = 'revenueLimitNotificationClosed';
enum RevenueLimitStageEnum {
  warning = 'warning',
  gracePeriod = 'gracePeriod',
  restricted = 'restricted',
}
const getRevenueLimitAccentClassName = (percentage: number) => {
  if (percentage >= 95) {
    return 'text-text-error';
  }
  if (percentage >= 90) {
    return 'text-text-error';
  }
  return 'text-text-warning';
};
export const RevenueLimitNotification = () => {
  const $ = _c(46);
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    totalRevenueLimit
  } = useTotalRevenueLimit();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      useSessionStorage: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const notificationState = useSessionStore(REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY, t0);
  let t1;
  if ($[1] !== notificationState) {
    t1 = () => notificationState.getItem() === true;
    $[1] = notificationState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [isClosed, setIsClosed] = useState(t1);
  let t2;
  if ($[3] !== totalRevenueLimit || $[4] !== user) {
    bb0: {
      if (!totalRevenueLimit || !user) {
        t2 = null;
        break bb0;
      }
      const revenueLimitInCents = totalRevenueLimit.maxLimit;
      const totalRevenueInCents = totalRevenueLimit.current;
      if (!revenueLimitInCents || totalRevenueInCents === null || totalRevenueInCents === undefined) {
        t2 = null;
        break bb0;
      }
      let t3;
      if ($[6] !== revenueLimitInCents || $[7] !== user.dashboardLocale) {
        t3 = currencyFormat(centsToUnits(revenueLimitInCents), CurrencyEnum.US_DOLLAR, user.dashboardLocale, true);
        $[6] = revenueLimitInCents;
        $[7] = user.dashboardLocale;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      const limit = t3;
      const percentage = Math.floor(totalRevenueInCents / revenueLimitInCents * 100);
      if (user.planLimitExceededAt && percentage >= MIN_REVENUE_LIMIT_PERCENTAGE) {
        const restrictionDate = user.restrictionEstimateDate ? new Date(user.restrictionEstimateDate) : null;
        if (restrictionDate && restrictionDate.getTime() > Date.now()) {
          t2 = {
            date: dateFormat(restrictionDate, {
              dateFormat: user.dateFormat,
              timeZone: user.timezone
            }),
            limit,
            percentage,
            stage: RevenueLimitStageEnum.gracePeriod as const
          };
          break bb0;
        }
        let t4;
        if ($[9] !== limit || $[10] !== percentage) {
          t4 = {
            limit,
            percentage,
            stage: RevenueLimitStageEnum.restricted as const
          };
          $[9] = limit;
          $[10] = percentage;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        t2 = t4;
        break bb0;
      }
      if (percentage < MIN_REVENUE_LIMIT_PERCENTAGE) {
        t2 = null;
        break bb0;
      }
      let t4;
      if ($[12] !== limit || $[13] !== percentage) {
        t4 = {
          limit,
          percentage,
          stage: RevenueLimitStageEnum.warning as const
        };
        $[12] = limit;
        $[13] = percentage;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      t2 = t4;
    }
    $[3] = totalRevenueLimit;
    $[4] = user;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const notificationData = t2;
  let t3;
  if ($[15] !== notificationState) {
    t3 = () => {
      notificationState.setItem(true);
      setIsClosed(true);
    };
    $[15] = notificationState;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const handleClose = t3;
  let t4;
  if ($[17] !== notificationData) {
    t4 = notificationData ?? {};
    $[17] = notificationData;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const {
    limit: limit_0,
    stage
  } = t4;
  const isDismissible = stage === RevenueLimitStageEnum.warning;
  if (!notificationData || isClosed && isDismissible) {
    return null;
  }
  let t5;
  if ($[19] !== notificationData.percentage) {
    t5 = getRevenueLimitAccentClassName(notificationData.percentage);
    $[19] = notificationData.percentage;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const accentClassName = t5;
  const t6 = notificationData.percentage >= 90 ? "error" : "warning";
  let t7;
  if ($[21] !== handleClose || $[22] !== isDismissible) {
    t7 = isDismissible ? <Button aria-label="Close revenue limit notification" size="icon-sm" variant="ghost-gray" onClick={handleClose}><XCloseIcon /></Button> : null;
    $[21] = handleClose;
    $[22] = isDismissible;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== accentClassName || $[25] !== limit_0 || $[26] !== notificationData.percentage || $[27] !== stage || $[28] !== t) {
    t8 = stage === RevenueLimitStageEnum.warning && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification" values={{
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className="link" href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <></>,
      percentage: <span className={accentClassName}>{notificationData.percentage}%</span>
    }} />;
    $[24] = accentClassName;
    $[25] = limit_0;
    $[26] = notificationData.percentage;
    $[27] = stage;
    $[28] = t;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== accentClassName || $[31] !== limit_0 || $[32] !== notificationData.date || $[33] !== stage || $[34] !== t) {
    t9 = stage === RevenueLimitStageEnum.gracePeriod && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_grace_period" values={{
      date: notificationData.date,
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className="link" href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <></>,
      span: <span className={accentClassName} />
    }} />;
    $[30] = accentClassName;
    $[31] = limit_0;
    $[32] = notificationData.date;
    $[33] = stage;
    $[34] = t;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== limit_0 || $[37] !== stage || $[38] !== t) {
    t10 = stage === RevenueLimitStageEnum.restricted && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_reached" values={{
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className="link" href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <></>
    }} />;
    $[36] = limit_0;
    $[37] = stage;
    $[38] = t;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t6 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t11 = <FullWidthBanner className="-mt-5 w-screen self-center" contentClassName={CONTENT_CLASSNAME} variant={t6} actions={t7}>{t8}{t9}{t10}</FullWidthBanner>;
    $[40] = t10;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
};